import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle } from 'lucide-react';
import PageHeader from '../common/PageHeader'; 
import PageBreadcrumb from '../common/PageBreadcrumb'; 
import DataTable from '../common/DataTable'; 
import { useToast } from '../common/Toast'; 
import complianceService from '../../services/complianceService'; 

const FrameworkRequirements = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [framework, setFramework] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await complianceService.getFrameworks();
        const found = res.data.find(f => f._id === id || f.id === id);
        if (found) {
          setFramework(found);
        } else {
          showError('Framework not found');
        }
      } catch (err) {
        showError('Failed to load framework details');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  const columns = [
    {
      key: 'code',
      label: 'Control Code',
      width: '15%',
      render: (value) => <span className="fw-bold text-dark">{value}</span>
    },
    {
      key: 'description',
      label: 'Requirement Description',
      width: '65%'
    },
    {
      key: 'riskLevel',
      label: 'Risk Level',
      width: '20%',
      render: (value) => {
        const colors = { 'High': 'danger', 'Critical': 'danger', 'Medium': 'warning', 'Low': 'success' };
        return <span className={`badge bg-${colors[value] || 'secondary'}`}>{value}</span>;
      }
    }
  ];

  const headerActions = [
    {
      label: 'Back',
      icon: <ArrowLeft size={18} />,
      onClick: () => navigate('/contracts/frameworks'),
      variant: 'outline'
    },
    // Link to the Add Requirement Screen
    {
        label: 'Add Requirement',
        icon: <Shield size={18} />, // Using Shield as generic icon or Plus
        onClick: () => navigate(`/contracts/frameworks/${id}/requirements/add`),
        variant: 'primary'
    }
  ];

  if (loading) return <div className="p-5 text-center">Loading...</div>;
  if (!framework) return <div className="p-5 text-center text-danger">Framework not found.</div>;

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title={`${framework.name} Requirements`}
        description={`List of controls for ${framework.code}`}
        actions={headerActions}
      />

      <div className="p-6">
        <div className="bg-white border rounded-lg shadow-sm">
          <DataTable
            data={framework.requirements || []}
            columns={columns}
            searchable={true}
            pagination={true}
            pageSize={10}
            emptyMessage="No requirements defined."
          />
        </div>
      </div>
    </>
  );
};

export default FrameworkRequirements;